import { usePowerMonitorService, useSensorAlerts, SensorType } from 'common';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSync } from './AppSyncContext';

export enum ElectricalAlertState {
  NoAlert = 'NoAlert',
  ServiceRequested = 'ServiceRequested',
  Alert = 'Alert',
}

interface ElectricalAlertContextType {
  alertState: ElectricalAlertState;
  alertMessage: string;
  powerStatus: string | null;
  onRequestService: () => Promise<void>;
  onClearAlert: () => Promise<void>;
  loading: boolean;
}

const ElectricalAlertContext = createContext<ElectricalAlertContextType | undefined>(undefined);

export const ElectricalAlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { sensors } = useAppSync();
  const { requestService, clearAlert } = usePowerMonitorService();

  const powerSensor = sensors.find((sensor) => sensor.type === SensorType.POWER);

  const {
    sensorAlerts,
    fetchAndUpdateSensorAlerts,
    loading: alertsLoading,
  } = useSensorAlerts(powerSensor?.id);

  const [alertState, setAlertState] = useState<ElectricalAlertState>(ElectricalAlertState.NoAlert);
  const [alertMessage, setAlertMessage] = useState('');
  const [powerStatus, setPowerStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeAlert, setActiveAlert] = useState<any>(null);

  useEffect(() => {
    if (powerSensor?.id) {
      fetchAndUpdateSensorAlerts(powerSensor.id);
    }
  }, [powerSensor?.id]);

  useEffect(() => {
    if (alertsLoading || sensors.length === 0) return;

    const powerMonitorSensorIds = sensors
      .filter((sensor) => sensor.type === SensorType.POWER)
      .map((sensor) => sensor.id);

    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

    const alert = sensorAlerts.find((alert) => {
      const isFromPowerSensor = powerMonitorSensorIds.includes(alert.sensorId);
      const isActiveStatus =
        alert.status === 'new' || alert.status === 'ongoing' || alert.status === 'pending';
      const isUnacknowledged = alert.acknowledgedAt === null && alert.acknowledgedBy === null;
      const isRecent = alert.timestamp && alert.timestamp * 1000 >= twentyFourHoursAgo;

      return isFromPowerSensor && isActiveStatus && isUnacknowledged && isRecent;
    });

    if (alert) {
      setActiveAlert(alert);
      setAlertState(ElectricalAlertState.Alert);
      setAlertMessage(alert.message || t('electrical.unexpectedPowerSurge'));

      // Determine specific power status based on alert types
      const hasHighVoltage = sensorAlerts.some((a) => a.alertType === 'HIGH_VOLTAGE');
      const hasLowVoltage = sensorAlerts.some((a) => a.alertType === 'LOW_VOLTAGE');
      const hasHighCurrent = sensorAlerts.some(
        (a) =>
          a.alertType === 'HIGH_CURRENT' ||
          a.alertType === 'HIGH_MCT_CURRENT' ||
          a.alertType === 'HIGH_BCT_CURRENT',
      );
      const hasLowCurrent = sensorAlerts.some(
        (a) =>
          a.alertType === 'LOW_CURRENT' ||
          a.alertType === 'LOW_MCT_CURRENT' ||
          a.alertType === 'LOW_BCT_CURRENT',
      );

      if (hasHighVoltage && hasHighCurrent) {
        setPowerStatus(t('electrical.status.powerSurge'));
      } else if (hasLowVoltage && hasLowCurrent) {
        setPowerStatus(t('electrical.status.lowPower'));
      }
    } else {
      setActiveAlert(null);
      setAlertState(ElectricalAlertState.NoAlert);
      setAlertMessage('');
      setPowerStatus(null);
    }
  }, [sensorAlerts.length, alertsLoading, sensors.length, sensorAlerts, sensors, t]);

  const handleRequestService = async () => {
    setLoading(true);
    try {
      if (activeAlert) {
        const alertSensor = sensors.find((s) => s.id === activeAlert.sensorId);
        if (alertSensor) {
          const resp = await requestService({
            sensorId: alertSensor.id,
            hubId: alertSensor.hubId,
          });
          if (resp) {
            setAlertState(ElectricalAlertState.ServiceRequested);
            setAlertMessage(t('electrical.serviceRequested'));
            if (powerSensor?.id) fetchAndUpdateSensorAlerts(powerSensor.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to request service:', error);
      setAlertMessage(t('electrical.failedToRequestService'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearAlert = async () => {
    setLoading(true);
    try {
      if (activeAlert) {
        // Find the sensor that has the active alert
        const alertSensor = sensors.find((s) => s.id === activeAlert.sensorId);
        if (alertSensor) {
          const resp = await clearAlert(alertSensor.id, alertSensor.hubId);
          if (resp) {
            setAlertState(ElectricalAlertState.NoAlert);
            setAlertMessage('');
            setActiveAlert(null);
            if (powerSensor?.id) await fetchAndUpdateSensorAlerts(powerSensor.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to clear alert:', error);
      setAlertMessage(t('electrical.failedToClearAlert'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ElectricalAlertContext.Provider
      value={{
        alertState,
        alertMessage,
        powerStatus,
        onRequestService: handleRequestService,
        onClearAlert: handleClearAlert,
        loading: loading || alertsLoading,
      }}>
      {children}
    </ElectricalAlertContext.Provider>
  );
};

export const useElectricalAlertContext = (): ElectricalAlertContextType => {
  const context = useContext(ElectricalAlertContext);
  if (!context) {
    throw new Error('useElectricalAlertContext must be used within an ElectricalAlertProvider');
  }
  return context;
};
